import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  emptyCustomerListFilters,
  type CustomerListFilters,
  type StrapiCustomerRow,
} from '../../../../api/strapiCrm';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from '../../../../components/DataGridNumberedPagination';
import { CustomerDataGrid } from '../../../../components/CustomerDataGrid';
import { useAuth } from '../../../../auth/AuthContext';
import { useCustomersListQuery, useDeleteCustomerMutation } from '../../../../query/hooks';
import { isMarketingPic } from '../../../QuotationsPage/quotationWorkflow';
import { LIST_FILTERS_APPLY_HINT, commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';

const CUSTOMER_FILTER_STATUS_ALL = 'ALL' as const;
const CUSTOMER_FILTER_STATUS_OPTIONS = [
  CUSTOMER_FILTER_STATUS_ALL,
  'active',
  'inactive',
  'prospect',
  'blocked',
] as const;

type CustomerFilterStatusValue = (typeof CUSTOMER_FILTER_STATUS_OPTIONS)[number];

const customerFilterStatusLabel: Record<CustomerFilterStatusValue, string> = {
  [CUSTOMER_FILTER_STATUS_ALL]: '(Semua status)',
  active: 'Aktif',
  inactive: 'Tidak aktif',
  prospect: 'Prospek',
  blocked: 'Diblokir',
};

type CustomerFilterDraft = {
  customerNumber: string;
  name: string;
  email: string;
  phone: string;
  status: CustomerFilterStatusValue;
};

const initialCustomerDraft: CustomerFilterDraft = {
  customerNumber: '',
  name: '',
  email: '',
  phone: '',
  status: CUSTOMER_FILTER_STATUS_ALL,
};

function cloneEmptyCustomerApplied(): CustomerListFilters {
  return { ...emptyCustomerListFilters };
}

function customerAppliedIsActive(f: CustomerListFilters): boolean {
  return (
    f.customerNumber.trim() !== '' ||
    f.name.trim() !== '' ||
    f.email.trim() !== '' ||
    f.phone.trim() !== '' ||
    f.status.trim() !== ''
  );
}

function draftToCustomerApplied(d: CustomerFilterDraft): CustomerListFilters {
  return {
    customerNumber: d.customerNumber.trim(),
    name: d.name.trim(),
    email: d.email.trim(),
    phone: d.phone.trim(),
    status: d.status === CUSTOMER_FILTER_STATUS_ALL ? '' : d.status,
  };
}

function draftLooksInitial(d: CustomerFilterDraft): boolean {
  return (
    d.customerNumber.trim() === '' &&
    d.name.trim() === '' &&
    d.email.trim() === '' &&
    d.phone.trim() === '' &&
    d.status === CUSTOMER_FILTER_STATUS_ALL
  );
}

/** Data grid: daftar customer dari Strapi. */
export function CustomerListTabScreen() {
  const { jwt, user } = useAuth();
  const navigate = useNavigate();
  const isPic = isMarketingPic(user);
  const picAssignee = useMemo(() => {
    if (!isPic || !user) return undefined;
    return { documentId: user.documentId, id: user.id };
  }, [isPic, user]);

  const [draft, setDraft] = useState<CustomerFilterDraft>(initialCustomerDraft);
  const [appliedFilters, setAppliedFilters] =
    useState<CustomerListFilters>(cloneEmptyCustomerApplied);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DATA_GRID_DEFAULT_PAGE_SIZE,
  });
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const commitFilters = useCallback(() => {
    setAppliedFilters(draftToCustomerApplied(draftRef.current));
  }, []);

  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [appliedFilters]);

  const { data, isPending, isError, error, refetch, isFetching } = useCustomersListQuery(
    jwt,
    appliedFilters,
    paginationModel.page + 1,
    paginationModel.pageSize,
    isPic ? { picAssignee } : undefined,
  );
  const deleteMut = useDeleteCustomerMutation(jwt);

  const [pendingDelete, setPendingDelete] = useState<StrapiCustomerRow | null>(null);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  function resetFilters(): void {
    setDraft(initialCustomerDraft);
    setAppliedFilters(cloneEmptyCustomerApplied());
  }

  const onView = useCallback(
    (row: StrapiCustomerRow) =>
      navigate(`/customer/${encodeURIComponent(row.documentId)}/view`),
    [navigate],
  );

  const onEdit = useCallback(
    (row: StrapiCustomerRow) => navigate(`/customer/${encodeURIComponent(row.documentId)}/edit`),
    [navigate],
  );

  const onDelete = useCallback((row: StrapiCustomerRow) => {
    deleteMut.reset();
    setPendingDelete(row);
  }, [deleteMut]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.documentId);
      setPendingDelete(null);
    } catch {
      /* surfaced in Alert */
    }
  }

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to="/customers/new"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon aria-hidden />}
          data-testid="customers-create-button"
        >
          Create a customer
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          startIcon={
            isFetching ? (
              <CircularProgress size={16} thickness={5} aria-label="Memuat ulang…" />
            ) : (
              <RefreshIcon aria-hidden />
            )
          }
          disabled={!jwt || isFetching}
          onClick={() => void refetch()}
          data-testid="customers-refresh-button"
        >
          Muat ulang
        </Button>
      </Stack>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Filter
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {LIST_FILTERS_APPLY_HINT}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="No. customer"
            value={draft.customerNumber}
            onChange={(e) => setDraft((d) => ({ ...d, customerNumber: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'customers-filter-customer-number' }}
          />
          <TextField
            label="Nama pelanggan"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'customers-filter-name' }}
          />
          <TextField
            label="Email"
            value={draft.email}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            type="search"
            inputProps={{ 'data-testid': 'customers-filter-email' }}
          />
          <TextField
            label="Telepon"
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="No. telp, WA, atau kantor (salah satu cocok)"
            inputProps={{ 'data-testid': 'customers-filter-phone' }}
          />
          <FormControl size="small" fullWidth sx={{ minWidth: 140 }}>
            <InputLabel id="customers-filter-status-label">Status</InputLabel>
            <Select
              labelId="customers-filter-status-label"
              label="Status"
              value={draft.status}
              onChange={(e) => {
                const v = e.target.value as CustomerFilterStatusValue;
                setDraft((d) => {
                  const next = { ...d, status: v };
                  draftRef.current = next;
                  setAppliedFilters(draftToCustomerApplied(next));
                  return next;
                });
              }}
              data-testid="customers-filter-status"
            >
              {CUSTOMER_FILTER_STATUS_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {customerFilterStatusLabel[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {customerAppliedIsActive(appliedFilters)
              ? `Halaman ini: ${rows.length} baris · Total terfilter (server): ${total}.`
              : `Halaman ini: ${rows.length} baris · Total pelanggan (server): ${total}.`}
          </Typography>
          <Button
            size="small"
            color="inherit"
            disabled={
              !customerAppliedIsActive(appliedFilters) && draftLooksInitial(draft)
            }
            onClick={resetFilters}
          >
            Reset filter
          </Button>
        </Stack>
      </Stack>

      <CustomerDataGrid
        rows={rows}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        isLoading={isPending || isFetching}
        isError={isError}
        error={error}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => {
          setPendingDelete(null);
          deleteMut.reset();
        }}
        maxWidth="xs"
        fullWidth
      >
        {pendingDelete ? (
          <>
            <DialogTitle>Hapus pelanggan?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Pelanggan <strong>{pendingDelete.name}</strong> ({pendingDelete.customer_number}) akan
                dihapus permanen. Tindakan ini tidak dapat dibatalkan.
              </Typography>
              {deleteMut.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {deleteMut.error instanceof Error
                    ? deleteMut.error.message
                    : 'Gagal menghapus pelanggan.'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button
                onClick={() => {
                  setPendingDelete(null);
                  deleteMut.reset();
                }}
                color="inherit"
              >
                Batal
              </Button>
              <Button
                color="error"
                variant="contained"
                disabled={deleteMut.isPending}
                onClick={() => void confirmDelete()}
              >
                Hapus
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
}
